<script setup lang="ts">
import 'vue3-carousel/dist/carousel.css'

import { Carousel, Navigation, Pagination, Slide } from 'vue3-carousel'

import { nftService } from '~/api/service/nft.service'
import NftCard from '~/components/@pages/nfts/NftCard.vue'
import DetailsBox from '~/components/box/DetailsBox.vue'
import PageTitleHeader from '~/components/header/PageTitleHeader.vue'
import { NFT_TRANSFER_COLUMN } from '~/components/table/tokens-transfer/tokensTransferColumn.const'
import TokensTransferTable from '~/components/table/tokens-transfer/TokensTransferTable.vue'
import BcPagination from '~/ui/Pagination/BcPagination.vue'
import { getBreadCrumb } from '~/utils/breadCrumbList'

// 프로젝트 breakpoints(sm: 840px, md: 1280px)와 동기화
const MOBILE_BREAKPOINT = 841
const carouselBreakpoints = {
  0: { itemsToShow: 1, snapAlign: 'start' as const },
  [MOBILE_BREAKPOINT]: { itemsToShow: 2, snapAlign: 'start' as const },
  1281: { itemsToShow: 2, snapAlign: 'start' as const },
}

// vue3-carousel v0.3.1 버그 우회: 초기 prop 기준으로 도트 수를 계산하고
// breakpoint 오버라이드 후에도 갱신하지 않으므로, 뷰포트에 맞는 초기값을 설정
const initialItemsToShow = ref(1)
const carouselKey = ref(0)

onMounted(() => {
  const shouldShowTwo = window.innerWidth >= MOBILE_BREAKPOINT
  if (shouldShowTwo) {
    initialItemsToShow.value = 2
    carouselKey.value++
  }
})

const { pagination, rowPerPageList } = usePagination({
  page: 1,
  rowPerPage: 25,
})

const { data, isLoading } = nftService.useGetNftTransferList(pagination)
const { data: nftCardData } = nftService.useGetNftCardList()

useSeoMeta(getMeta('All NFTs', 'View the Latest NFT Transfers'))

useHead(getBreadCrumb())
</script>

<template>
  <div class="flex flex-col gap-y-8">
    <div class="flex flex-col gap-y-4">
      <PageTitleHeader title="All NFTs" />
      <div v-if="nftCardData" class="nft-carousel-wrapper">
        <Carousel
          :key="carouselKey"
          :items-to-show="initialItemsToShow"
          :items-to-scroll="1"
          :wrap-around="false"
          :mouse-drag="false"
          :touch-drag="true"
          snap-align="start"
          :breakpoints="carouselBreakpoints"
        >
          <Slide v-for="nft in nftCardData.nftCardList" :key="nft.symbol" class="nft-carousel-slide">
            <NftCard :nft="nft" class="h-full" />
          </Slide>
          <template #addons>
            <Navigation />
            <Pagination />
          </template>
        </Carousel>
      </div>
    </div>
    <div class="flex flex-col gap-y-4">
      <PageTitleHeader title="NFT Transfers" />
      <div>
        <DetailsBox class="mb-2">
          Showing the last <span class="text-primary">{{ data?.formattedViewCount }}</span> records from {{
            data?.formattedTotalCount
          }}
          results
          <template #pagination>
            <BcPagination
              v-model="pagination"
              :row-per-page-list="rowPerPageList"
              :total-count="data?.viewCount"
              hide-rows-per-page
            />
          </template>
        </DetailsBox>
        <Card>
          <TokensTransferTable
            :rows="data?.transferList || []"
            :is-loading="isLoading"
            :row-cnt="pagination.rowPerPage"
            :columns="NFT_TRANSFER_COLUMN"
          />
          <BcPagination
            v-model="pagination"
            :row-per-page-list="rowPerPageList"
            :total-count="data?.viewCount"
          />
        </Card>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.nft-carousel-wrapper {
  @apply overflow-hidden;

  // 화살표: 배경 제거, chevron만 표시
  --vc-nav-color: rgba(255, 255, 255, 0.5);
  --vc-nav-color-hover: #5c8ffa;
  --vc-nav-width: 20px;
  --vc-nav-height: 20px;
  --vc-nav-border-radius: 0;
  --vc-nav-background: transparent;
  --vc-pgn-background-color: rgba(255, 255, 255, 0.2);
  --vc-pgn-active-color: #5c8ffa;
  --vc-pgn-width: 8px;
  --vc-pgn-height: 8px;
  --vc-pgn-border-radius: 50%;

  // flex-wrap: viewport 전폭 → 컨트롤 다음 줄 중앙 정렬
  :deep(.carousel) {
    @apply flex flex-wrap justify-center items-center;
    gap: 16px 12px;
  }

  :deep(.carousel__viewport) {
    @apply w-full;
  }

  :deep(.carousel__slide) {
    @apply text-left;
  }

  // [<] [도트] [>] 중앙 그룹화: order로 배치 순서 보장
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    @apply static transform-none;
  }

  :deep(.carousel__prev) { order: 1; }
  :deep(.carousel__pagination) { @apply m-0; order: 2; }
  :deep(.carousel__next) { order: 3; }
}

.nft-carousel-slide {
  @apply px-2 self-stretch;
  @include sm { @apply px-0; }
}
</style>
